import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import MetricCard from "../../performance/MetricCard";
import {
  PerformanceContainer,
  MetricsGrid,
} from "../../performance/PerformanceStyles";
import {
  fetchRevenueMetrics,
  fetchDateYearMetrics,
} from "store/performance-report/PerformanceReportActions";
import { useSetGoalHandler } from "hooks/useSetGoalHandler";
import {
  selectPerformanceReportStoreId,
  selectPerformanceReportDateRange,
  selectIsReportAPIAvailable,
  selectRevenueMetrics,
  selectDateYearMetrics,
  selectIsRevenueMetricsAlreadyFetched,
} from "store/performance-report/PerformanceReportSelectors";
import {
  selectKpiCategoryById,
  selectKpiConfigIsLoaded,
} from "store/kpi-config/KpiConfigSelectors";
import {
  getBasicMetricsConfig,
  convertBasicMetricsToPayload,
  getAllMetricIds,
  getMetricsByDisplayType,
  getMetricGoal,
} from "utils/KpiConfigUtils";
import {
  calculateDateRanges,
  transformMetricsData,
  getDaysFromPeriodType,
  calculateDimensionId,
  calculateGranularity,
  createMetricLineCharts,
} from "utils/PerformanceReportUtils";

const RevenueComponent = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const performanceDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const revenueMetricsData = useSelector(selectRevenueMetrics);
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const revenueCategory = useSelector((state: any) =>
    selectKpiCategoryById("revenue")(state),
  );
  const isAlreadyFetched = useSelector(selectIsRevenueMetricsAlreadyFetched);

  const revenueMetricsConfig =
    isKpiConfigLoaded && revenueCategory
      ? getBasicMetricsConfig(revenueCategory)
      : [];

  const revenueMetrics = transformMetricsData(
    revenueMetricsConfig,
    revenueMetricsData,
    intl,
  );
  const dateYearMetricsData = useSelector(selectDateYearMetrics);
  const dateRangeDays = getDaysFromPeriodType(performanceDateRange);
  const dimensionId = calculateDimensionId(dateRangeDays);
  const granularity = calculateGranularity(dateRangeDays);
  const metricLineCharts = createMetricLineCharts(
    dateYearMetricsData,
    revenueMetricsConfig,
    granularity,
    intl,
    "revenue",
  );

  useEffect(() => {
    if (isAlreadyFetched) {
      return;
    }

    if (
      performanceStoreId &&
      performanceDateRange &&
      isReportAPIAvailable &&
      isKpiConfigLoaded &&
      revenueCategory
    ) {
      const { currentPeriod, comparisonPeriod } =
        calculateDateRanges(performanceDateRange);
      const basicMetrics = getMetricsByDisplayType(revenueCategory, "basic");
      const metricsPayload = convertBasicMetricsToPayload(basicMetrics);

      dispatch(
        fetchRevenueMetrics(
          performanceStoreId,
          currentPeriod,
          comparisonPeriod,
          metricsPayload,
        ),
      );
      const allMetricIds = getAllMetricIds(basicMetrics);
      dispatch(
        fetchDateYearMetrics({
          storeId: performanceStoreId,
          dateRange: currentPeriod,
          dimension: { id: dimensionId },
          metrics: allMetricIds,
        }),
      );
    }
  }, [
    dispatch,
    performanceStoreId,
    performanceDateRange,
    isReportAPIAvailable,
    isKpiConfigLoaded,
    revenueCategory,
    dateRangeDays,
    dimensionId,
    isAlreadyFetched,
  ]);

  const handleSetGoal = useSetGoalHandler("revenue");

  return (
    <>
      {performanceStoreId && performanceDateRange && (
        <PerformanceContainer>
          <MetricsGrid>
            {revenueMetrics.map((metric) => (
              <MetricCard
                key={metric.id}
                data={metric}
                lineChartData={metricLineCharts[metric.id]}
                onSetGoal={handleSetGoal}
                goal={getMetricGoal(revenueCategory, metric.id)}
              />
            ))}
          </MetricsGrid>
        </PerformanceContainer>
      )}
    </>
  );
};

export default RevenueComponent;
